import React from 'react';
import moment from 'moment';
import {
  EcoDownSvgIcon,
  EcoTopSvgIcon,
  ElectricDownSvgIcon,
  ElectricTopSvgIcon,
  GeothermalDownSvgIcon,
  GeothermalTopSvgIcon,
  SolarDownSvgIcon,
  SolarTopSvgIcon,
  WindDownSvgIcon,
  WindTopSvgIcon,
} from '../components/common/IconProvider';
import store from '../store';
/**
 * Utility function to format dates or date ranges using moment.js
 * @param {string|Date} date - The date to format.
 * @param {string} format - The format string for moment.js.
 * @param {string} defaultValue - The default value to return if date is invalid or null.
 * @param {string} [endDate] - The end date for date ranges.
 * @returns {string} - The formatted date string or the default value.
 */
export const formatDate = (
  date,
  format = 'DD MMM YYYY',
  defaultValue = 'NA',
  endDate = null,
) => {
  if (endDate) {
    // Handle date range formatting
    const startYear = moment(date).year();
    const endYear = moment(endDate).year();
    return `${startYear} - ${endYear}`;
  }

  if (!date) return defaultValue;
  return moment(date).format(format) || defaultValue;
};

// Utility function to handle substrings with fallback
export const formatWithEllipsis = (text, maxLength, defaultValue = 'NA') => {
  return text
    ? text.substr(0, maxLength) + (text.length > maxLength ? '...' : '')
    : defaultValue;
};

export const calculateLifeSpanDifference = (date) => {
  return Math.max(moment(date).year() - moment().year(), 0);
};

// Utility function to check KYC status
export const isKycStatusPending = (kycData) => {
  return kycData.some(
    (status) => status === 'awaiting_data' || status === 'awaiting_approval',
  );
};

export const formatNumber = (
  value,
  {
    style = 'currency',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = {},
) => {
  const locale = store.getState().languageReducer.language;

  let amount = Number(value);

  // Return default value for 0, null or undefined
  if (value === 0 || value == null || value === '') return '0';

  // Determine the locale to use for formatting
  const formatLocale = style === 'decimal' ? 'en-US' : locale;

  // Default options for number formatting
  const formatOptions = {
    style: style,
    minimumFractionDigits: minimumFractionDigits,
    maximumFractionDigits: maximumFractionDigits,
    currency: 'EUR',
  };

  return new Intl.NumberFormat(formatLocale, formatOptions).format(amount);
};

// Utility function for calculating energy capacity and formatting the result
export const calculateEnergyCapacity = (
  energyCapacity,
  availableForSale,
  quantity = 1,
  decimalPlaces = 5,
  defaultValue = 'NA',
) => {
  if (!energyCapacity || !availableForSale) return defaultValue;

  const result =
    (parseFloat(energyCapacity) / parseFloat(availableForSale)) * quantity;

  return result.toFixed(decimalPlaces);
};

// Utility function to calculate and format the discount amount
export const calculateDiscountAmount = (
  totalRemainToken,
  noOfTokens,
  pricePerToken,
  decimalPlaces = 2,
) => {
  // Determine the amount to be discounted based on the remaining tokens and tokens requested
  const discountAmount =
    totalRemainToken > noOfTokens
      ? noOfTokens * pricePerToken
      : totalRemainToken * pricePerToken;

  // Return the formatted discount amount
  return parseFloat(discountAmount.toFixed(decimalPlaces));
};

// Utility function for validating image files
export const validateImageFile = (
  file,
  allowedExtensions = /\.(jpg|jpeg|png|gif)$/,
  maxSizeMB = 20,
) => {
  if (!file) {
    return { valid: false, error: 'Please select a profile image' };
  }

  // Check file extension
  if (!file.name.match(allowedExtensions)) {
    return { valid: false, error: 'Please select a valid profile image' };
  }

  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return {
      valid: false,
      error: `Profile image size should be less than ${maxSizeMB} MB`,
    };
  }

  return { valid: true };
};

export const DynamicEnergyBarTopSvgIcon = ({ slug }) => {
  const renderIcon = () => {
    switch (slug) {
      case 'eco':
        return <EcoTopSvgIcon />;
      case 'electric':
        return <ElectricTopSvgIcon />;
      case 'geothermal':
        return <GeothermalTopSvgIcon />;
      case 'solar':
        return <SolarTopSvgIcon />;
      case 'wind':
        return <WindTopSvgIcon />;
      default:
        return null;
    }
  };

  return renderIcon();
};
export const DynamicEnergyBarDownIcon = ({ slug }) => {
  const renderIcon = () => {
    switch (slug) {
      case 'eco':
        return <EcoDownSvgIcon />;
      case 'electric':
        return <ElectricDownSvgIcon />;
      case 'geothermal':
        return <GeothermalDownSvgIcon />;
      case 'solar':
        return <SolarDownSvgIcon />;
      case 'wind':
        return <WindDownSvgIcon />;
      default:
        return null;
    }
  };

  return renderIcon();
};

export const ShowStatus = (status) => {
  const statusClasses =
    status === 1
      ? 'bg-[#97c05b]'
      : status === 0
        ? 'bg-[#ffc107]'
        : status === 2
          ? 'bg-[#f44336]'
          : ' ';

  return statusClasses;
};
